import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, GripVertical, Eye, EyeOff, Save, X } from 'lucide-react';


import { API_URL } from '../../config/api';
const API = API_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'file', label: 'File/Image' }
];

export default function CustomFieldsManager({ user, onLogout }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    field_type: 'text',
    placeholder: '',
    help_text: '',
    required: false,
    default_value: '',
    visibility: 'student_visible',
    dropdown_options: []
  });
  const [dropdownOption, setDropdownOption] = useState('');

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      const res = await axios.get(`${API}/custom-fields?include_hidden=true`, { headers: getAuthHeaders() });
      setFields(res.data);
    } catch (error) {
      console.error('Failed to fetch fields:', error);
      toast.error('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingField(null);
    setFormData({
      label: '',
      field_type: 'text',
      placeholder: '',
      help_text: '',
      required: false,
      default_value: '',
      visibility: 'student_visible',
      dropdown_options: []
    });
    setShowAddModal(true);
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setFormData({
      label: field.label,
      field_type: field.field_type,
      placeholder: field.placeholder || '',
      help_text: field.help_text || '',
      required: field.required,
      default_value: field.default_value || '',
      visibility: field.visibility,
      dropdown_options: field.dropdown_options || []
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.label.length < 2 || formData.label.length > 100) {
      toast.error('Label must be between 2 and 100 characters');
      return;
    }

    if (formData.field_type === 'dropdown' && formData.dropdown_options.length === 0) {
      toast.error('Please add at least one option for dropdown field');
      return;
    }

    try {
      if (editingField) {
        await axios.put(`${API}/custom-fields/${editingField.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Field updated successfully');
      } else {
        await axios.post(`${API}/custom-fields`, formData, { headers: getAuthHeaders() });
        toast.success('Field created successfully');
      }
      
      setShowAddModal(false);
      fetchFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save field');
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Are you sure you want to delete this field? All user data for this field will be lost.')) {
      return;
    }

    try {
      await axios.delete(`${API}/custom-fields/${fieldId}`, { headers: getAuthHeaders() });
      toast.success('Field deleted successfully');
      fetchFields();
    } catch (error) {
      toast.error('Failed to delete field');
    }
  };

  const handleAddDropdownOption = () => {
    if (dropdownOption.trim()) {
      setFormData(prev => ({
        ...prev,
        dropdown_options: [...prev.dropdown_options, dropdownOption.trim()]
      }));
      setDropdownOption('');
    }
  };

  const handleRemoveDropdownOption = (index) => {
    setFormData(prev => ({
      ...prev,
      dropdown_options: prev.dropdown_options.filter((_, i) => i !== index)
    }));
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (dragIndex === dropIndex) return;

    const newFields = [...fields];
    const [removed] = newFields.splice(dragIndex, 1);
    newFields.splice(dropIndex, 0, removed);
    
    setFields(newFields);

    try {
      await axios.put(`${API}/custom-fields/reorder`, {
        field_ids: newFields.map(f => f.id)
      }, { headers: getAuthHeaders() });
      toast.success('Fields reordered successfully');
    } catch (error) {
      toast.error('Failed to reorder fields');
      fetchFields();
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <p className="text-[#4D4D4D]">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-navy">Custom Fields Manager</h1>
            <p className="text-[#4D4D4D] mt-1">Manage custom fields for user profiles</p>
          </div>
          <Button onClick={handleOpenAddModal} className="bg-primary-blue hover:bg-[#1874CD]">
            <Plus className="w-4 h-4 mr-2" />
            Add Custom Field
          </Button>
        </div>

        {/* Fields List */}
        {fields.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-[#4D4D4D] mb-4">No custom fields created yet</p>
              <Button onClick={handleOpenAddModal} variant="outline">
                Create Your First Field
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6">
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    className="flex items-center justify-between p-4 bg-[#F7F7F7] rounded-xl hover:bg-[#EEEEEE] transition-all cursor-move group"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <GripVertical className="w-5 h-5 text-[#A5A5A5] group-hover:text-[#4D4D4D]" />
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-navy">{field.label}</h3>
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                            {FIELD_TYPES.find(t => t.value === field.field_type)?.label}
                          </span>
                          {field.required && (
                            <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#4D4D4D] mt-1">{field.help_text || 'No help text'}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {field.visibility === 'student_visible' ? (
                        <Eye className="w-4 h-4 text-green-600" title="Visible to students" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-orange-600" title="Admin only" />
                      )}
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditField(field)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteField(field.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Field Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-navy">
                {editingField ? 'Edit Custom Field' : 'Add Custom Field'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Label */}
              <div>
                <Label htmlFor="label">Field Label *</Label>
                <Input
                  id="label"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="e.g., House Name"
                  required
                  maxLength={100}
                />
                <p className="text-xs text-[#4D4D4D] mt-1">{formData.label.length}/100 characters</p>
              </div>

              {/* Field Type */}
              <div>
                <Label htmlFor="field_type">Field Type *</Label>
                <Select value={formData.field_type} onValueChange={(value) => setFormData({ ...formData, field_type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dropdown Options */}
              {formData.field_type === 'dropdown' && (
                <div>
                  <Label>Dropdown Options *</Label>
                  <div className="flex space-x-2 mb-2">
                    <Input
                      value={dropdownOption}
                      onChange={(e) => setDropdownOption(e.target.value)}
                      placeholder="Enter an option"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddDropdownOption();
                        }
                      }}
                    />
                    <Button type="button" onClick={handleAddDropdownOption}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {formData.dropdown_options.map((option, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">{option}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveDropdownOption(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Placeholder */}
              <div>
                <Label htmlFor="placeholder">Placeholder Text</Label>
                <Input
                  id="placeholder"
                  value={formData.placeholder}
                  onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                  placeholder="e.g., Enter your house name"
                />
              </div>

              {/* Help Text */}
              <div>
                <Label htmlFor="help_text">Help Text</Label>
                <Input
                  id="help_text"
                  value={formData.help_text}
                  onChange={(e) => setFormData({ ...formData, help_text: e.target.value })}
                  placeholder="Additional guidance for users"
                />
              </div>

              {/* Default Value */}
              <div>
                <Label htmlFor="default_value">Default Value</Label>
                <Input
                  id="default_value"
                  value={formData.default_value}
                  onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                  placeholder="Optional default value"
                />
              </div>

              {/* Required */}
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.required}
                  onCheckedChange={(checked) => setFormData({ ...formData, required: checked })}
                />
                <Label>Required Field</Label>
              </div>

              {/* Visibility */}
              <div>
                <Label>Visibility</Label>
                <Select value={formData.visibility} onValueChange={(value) => setFormData({ ...formData, visibility: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student_visible">Visible to Students</SelectItem>
                    <SelectItem value="admin_only">Admin Only (Hidden from Students)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Actions */}
              <div className="flex space-x-2 pt-4">
                <Button type="submit" className="flex-1 bg-primary-blue hover:bg-[#1874CD]">
                  <Save className="w-4 h-4 mr-2" />
                  {editingField ? 'Update Field' : 'Create Field'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
